import { useState, useCallback, useEffect, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Paperclip, Download, FileText, X, Loader2, MapPin, ExternalLink, MessageCircleReply, AlertTriangle } from 'lucide-react'
import { Message } from '@/hooks/useConversationMessages'
import { parseWhatsAppFormatting, formatFileSize } from './messageUtils'
import { instagramShareStatus } from './inboxMessageUtils'
import { TemplateMessagePreview } from './TemplateMessagePreview'
import { UserInteraction } from './UserInteraction'
import { InteractiveMessage } from './InteractiveMessage'
import { ProductList } from './ProductList'
import { Product } from './Product'
import { OrderPlaced } from './OrderPlaced'
import { ListMessage } from './ListMessage'
import { getMediaIcon } from './messageHelpers'
import { getUnsupportedMessageInfo } from './unsupportedMessage'

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
    </svg>
  )
}

function useFileDownload(mediaUrl: string, filename: string) {
  return useCallback(async (e?: React.MouseEvent) => {
    e?.stopPropagation()
    try {
      const response = await fetch(mediaUrl)
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch {
      window.open(mediaUrl, '_blank')
    }
  }, [mediaUrl, filename])
}

function PreviewModal({
  open,
  onOpenChange,
  mediaUrl,
  filename,
  kind,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  mediaUrl: string
  filename: string
  kind: 'pdf' | 'image' | 'office' | null
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const blobUrlRef = useRef<string | null>(null)
  const handleDownload = useFileDownload(mediaUrl, filename)

  useEffect(() => {
    if (!open || blobUrl) return
    if (kind !== 'pdf' && kind !== 'image') return

    let cancelled = false
    setLoading(true)
    fetch(mediaUrl)
      .then(r => r.blob())
      .then(blob => {
        if (cancelled) return
        const url = window.URL.createObjectURL(blob)
        blobUrlRef.current = url
        setBlobUrl(url)
      })
      .catch(() => { if (!cancelled) setBlobUrl(mediaUrl) })
      .finally(() => { if (!cancelled) setLoading(false) })

    return () => { cancelled = true }
  }, [open, mediaUrl, kind, blobUrl])

  useEffect(() => () => {
    if (blobUrlRef.current) window.URL.revokeObjectURL(blobUrlRef.current)
  }, [])

  const officeViewerUrl = kind === 'office'
    ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(mediaUrl)}`
    : null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-[90vw] h-[85vh] p-0 gap-0 overflow-hidden [&>button:last-child]:hidden">
        <DialogTitle className="sr-only">{filename}</DialogTitle>

        <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50 flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {getMediaIcon('document')}
            <span className="text-sm font-medium truncate">{filename}</span>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0 ml-4">
            <Button variant="outline" size="sm" onClick={handleDownload}>
              <Download className="h-4 w-4 mr-1.5" />
              Download
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 rounded-full hover:bg-slate-50"
              onClick={() => onOpenChange(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-auto bg-gray-100 flex items-center justify-center" style={{ height: 'calc(85vh - 57px)' }}>
          {loading ? (
            <div className="flex flex-col items-center gap-2 text-gray-500">
              <Loader2 className="h-8 w-8 animate-spin" />
              <span className="text-sm">Loading preview...</span>
            </div>
          ) : kind === 'image' && blobUrl ? (
            <img src={blobUrl} alt={filename} className="max-w-full max-h-full object-contain" />
          ) : kind === 'pdf' && blobUrl ? (
            <iframe src={blobUrl} className="w-full h-full border-0" title={filename} />
          ) : kind === 'office' && officeViewerUrl ? (
            <iframe src={officeViewerUrl} className="w-full h-full border-0" title={filename} />
          ) : (
            <div className="flex flex-col items-center gap-3 text-gray-500 p-8">
              <FileText className="h-12 w-12" />
              <p className="text-sm font-medium">No preview available</p>
              <p className="text-xs text-gray-400">Download the file to view it on your device</p>
              <Button variant="outline" size="sm" onClick={handleDownload}>
                <Download className="h-4 w-4 mr-1.5" />
                Download
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

function getPreviewKind(filename: string, mimeType?: string | null): 'pdf' | 'image' | 'office' | null {
  const name = (filename || '').toLowerCase()
  const mime = (mimeType || '').toLowerCase()
  if (name.endsWith('.pdf') || mime === 'application/pdf') return 'pdf'
  if (/\.(jpe?g|png|gif|webp|bmp|svg)$/.test(name) || mime.startsWith('image/')) return 'image'
  if (/\.(xlsx?|docx?|pptx?|csv|tsv)$/.test(name) ||
      /spreadsheet|wordprocessing|presentation|ms-excel|ms-word|ms-powerpoint/.test(mime)) return 'office'
  return null
}

function DocumentMessage({ message, mediaUrl, mediaType }: { message: Message; mediaUrl: string; mediaType?: string | null }) {
  const [open, setOpen] = useState(false)
  const filename = (message as any).mediaFilename || 'Document'
  const previewKind = getPreviewKind(filename, mediaType)
  const handleDownload = useFileDownload(mediaUrl, filename)

  return (
    <div className="space-y-2 p-2">
      <div
        className="flex items-center gap-2 p-3 bg-white rounded-lg shadow-sm border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setOpen(true)}
      >
        {getMediaIcon(mediaType || 'document')}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{filename}</p>
          <p className="text-xs text-muted-foreground">
            {mediaType?.toUpperCase()}
            {(message as any).mediaSize && ` • ${formatFileSize((message as any).mediaSize)}`}
          </p>
        </div>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 flex-shrink-0" title="Download" onClick={handleDownload}>
          <Download className="h-4 w-4 text-gray-500" />
        </Button>
      </div>

      <PreviewModal open={open} onOpenChange={setOpen} mediaUrl={mediaUrl} filename={filename} kind={previewKind} />

      {message.content_text && (
        <p className="text-sm whitespace-pre-wrap px-2 text-gray-900">
          {parseWhatsAppFormatting(message.content_text)}
        </p>
      )}
    </div>
  )
}

function ImagePreview({ mediaUrl, alt, message, maxHeightClass = 'max-h-48' }: { mediaUrl: string; alt: string; message: Message; maxHeightClass?: string }) {
  const [open, setOpen] = useState(false)
  const filename = (message as any).mediaFilename || `${alt}.jpg`

  return (
    <>
      <div className="relative bg-white rounded-lg overflow-hidden shadow-sm border border-gray-200">
        <img
          src={mediaUrl}
          alt={alt}
          loading="lazy"
          decoding="async"
          className={`max-w-xs ${maxHeightClass} w-full h-auto object-contain cursor-pointer hover:opacity-90 transition-opacity`}
          onClick={() => setOpen(true)}
          onError={(e) => {
            const target = e.target as HTMLImageElement
            target.style.display = 'none'
            const parent = target.parentElement
            if (parent && !parent.querySelector('.error-message')) {
              const errorDiv = document.createElement('div')
              errorDiv.className = 'error-message p-4 text-center text-sm text-muted-foreground'
              errorDiv.textContent = `${alt} failed to load`
              parent.appendChild(errorDiv)
            }
          }}
          style={{ backgroundColor: 'white', minHeight: '100px' }}
        />
      </div>
      <PreviewModal open={open} onOpenChange={setOpen} mediaUrl={mediaUrl} filename={filename} kind="image" />
    </>
  )
}

function toSafeHttpUrl(value: string): string {
  if (!value) return ''
  try {
    const parsed = new URL(value)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? value : ''
  } catch {
    return ''
  }
}

function LocationMessage({ message }: { message: Message }) {
  const [mapImageFailed, setMapImageFailed] = useState(false)

  let lat: number | null = null
  let lng: number | null = null
  let locName = ''
  let locAddress = ''
  let sharedUrl = ''
  let caption = ''
  let statusTitle = ''
  let statusText = ''
  let mapImageUrl = ''

  try {
    const parsed = JSON.parse(message.content_text || '{}')
    lat = parseFloat(parsed.latitude)
    lng = parseFloat(parsed.longitude)
    locName = typeof parsed.name === 'string' ? parsed.name : ''
    locAddress = typeof parsed.address === 'string' ? parsed.address : ''
    sharedUrl = typeof parsed.url === 'string' ? parsed.url : ''
    caption = typeof parsed.caption === 'string' ? parsed.caption : ''
    statusTitle = typeof parsed.statusTitle === 'string' ? parsed.statusTitle : ''
    statusText = typeof parsed.statusText === 'string' ? parsed.statusText : ''
    mapImageUrl = typeof parsed.mapImageUrl === 'string' ? parsed.mapImageUrl : ''
  } catch {
  }

  const hasCoords =
    lat !== null && lng !== null &&
    Number.isFinite(lat) && Number.isFinite(lng) &&
    Math.abs(lat) <= 90 && Math.abs(lng) <= 180

  const safeSharedUrl = toSafeHttpUrl(sharedUrl)
  const safeMapImageUrl = toSafeHttpUrl(mapImageUrl)
  const showMapImage = !!safeMapImageUrl && !mapImageFailed

  const placeQuery = [locName, locAddress].filter(Boolean).join(', ')

  const mapsUrl = hasCoords
    ? `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
    : safeSharedUrl
      ? safeSharedUrl
      : placeQuery
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(placeQuery)}`
        : ''

  // Meta's statusTitle/statusText are in the sender's Instagram language, so
  // they only tell us this is a share; the words shown are ours.
  const shareStatus = statusTitle || statusText ? instagramShareStatus(hasCoords || !!safeMapImageUrl) : null
  const title = locName || shareStatus?.title || 'Shared location'
  const subtitle =
    locAddress ||
    shareStatus?.text ||
    (hasCoords ? `${lat!.toFixed(5)}, ${lng!.toFixed(5)}` : '')

  const body = (
    <>
      {showMapImage ? (
        <img
          src={safeMapImageUrl}
          alt="Map of the shared location"
          loading="lazy"
          className="h-[140px] w-full object-cover"
          onError={() => setMapImageFailed(true)}
          data-testid="location-map-image"
        />
      ) : (
        <div
          className={`relative flex h-[104px] items-center justify-center overflow-hidden ${hasCoords || mapsUrl
            ? 'bg-[linear-gradient(135deg,#eef6ee_0%,#e6f0f7_100%)]'
            : 'bg-[#F2F4F7]'
            }`}
        >
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.35]"
            style={{
              backgroundImage:
                'linear-gradient(to right, rgba(148,163,184,0.35) 1px, transparent 1px),' +
                'linear-gradient(to bottom, rgba(148,163,184,0.35) 1px, transparent 1px)',
              backgroundSize: '22px 22px',
            }}
          />
          <span className="relative flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-sm">
            <MapPin className="h-6 w-6 text-red-500" />
          </span>
        </div>
      )}

      <div className="bg-white px-3 py-2">
        <p className="truncate text-sm font-medium text-gray-900">{title}</p>
        {subtitle && <p className="truncate text-xs text-gray-500">{subtitle}</p>}
        {caption && <p className="mt-1 break-words text-xs text-gray-600">{caption}</p>}
        {mapsUrl ? (
          <span className="mt-1.5 flex items-center gap-1 text-xs font-medium text-blue-600">
            <ExternalLink className="h-3 w-3" />
            {hasCoords || !safeSharedUrl ? 'Open in Google Maps' : 'Open location'}
          </span>
        ) : statusTitle || statusText ? null : (
          <p className="mt-1 text-xs text-gray-500">
            Instagram didn't share the map details — open the chat in Instagram to see it.
          </p>
        )}
      </div>
    </>
  )

  const shell = 'w-[280px] overflow-hidden rounded-2xl border border-[#EAECF0] bg-white'

  if (!mapsUrl) {
    return (
      <div className={shell} data-testid="location-message">
        {body}
      </div>
    )
  }

  return (
    <a
      href={mapsUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`${shell} block transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500`}
      data-testid="location-message"
      aria-label={`Open ${title} in Google Maps`}
    >
      {body}
    </a>
  )
}

interface MediaContentProps {
  message: Message
  businessAccountId?: string
}

export function MediaContent({ message, businessAccountId }: MediaContentProps) {
  try {
    const ct = (message.content_text || '').trim()
    if (ct.startsWith('{')) {
      const cd = JSON.parse(ct)
      if (cd?.type === 'cta_url' || cd?.type === 'ctaurl') {
        return <InteractiveMessage message={message} />
      }
    }
  } catch (e) {
  }

  if (message.message_type?.toLowerCase() === 'text') {
    const textContent = message.content_text || ''
    if (!textContent || !textContent.trim()) {
      const hasMedia = message.media_url || (message as any).mediaUrl
      if (!hasMedia) {
        return <div className="text-sm text-gray-500 italic">No text content</div>
      }
    }
    return (
      <div className="text-sm whitespace-pre-wrap leading-relaxed text-gray-900">
        {textContent.split('\n').map((line, index) => (
          <div key={index} className="mb-1">
            {parseWhatsAppFormatting(line)}
          </div>
        ))}
      </div>
    )
  }

  if (message.message_type?.toLowerCase() === 'product_list' || message.message_type?.toLowerCase() === 'product') {
    let interactiveData = null
    let displayText = message.content_text || ''
    
    try {
      const contentData = JSON.parse(message.content_text || '{}')
      if (contentData.interactiveData && typeof contentData.interactiveData === 'object') {
        interactiveData = contentData.interactiveData
        displayText = contentData.text || displayText
      }
    } catch (e) {
      displayText = message.content_text || ''
    }

    if (interactiveData && typeof interactiveData === 'object') {
      if (interactiveData.type === 'product_list') {
        return <ProductList interactiveData={interactiveData} />
      }

      if (interactiveData.type === 'product') {
        return <Product interactiveData={interactiveData} />
      }
    }

    return (
      <div className="text-sm whitespace-pre-wrap leading-relaxed text-gray-900">
        {displayText || 'Product message'}
      </div>
    )
  }

  if (message.message_type?.toLowerCase() === 'interactive' || message.message_type?.toLowerCase() === 'button') {
    if (message.is_inbound) {
      return <UserInteraction message={message} />
    }

    const mediaType = message.media_type || (message as any).mediaType
    if (mediaType === 'list') {
      return <ListMessage message={message} />
    }

    try {
      const contentData = JSON.parse(message.content_text || '{}')
      const sections = contentData.sections || contentData.action?.sections
      if (sections && Array.isArray(sections) && sections.length > 0) {
        return <ListMessage message={message} />
      }
      if (contentData.type === 'list') {
        return <ListMessage message={message} />
      }
    } catch (e) {
    }

    try {
      const contentData = JSON.parse(message.content_text || '{}')
      if (contentData.interactiveData && typeof contentData.interactiveData === 'object') {
        const interactiveData = contentData.interactiveData
        
        if (interactiveData.type === 'product_list') {
          return <ProductList interactiveData={interactiveData} />
        }

        if (interactiveData.type === 'product') {
          return <Product interactiveData={interactiveData} />
        }

        if (interactiveData.type === 'order_placed') {
          return <OrderPlaced interactionData={interactiveData} />
        }
      }
    } catch (e) {
    }

    return <InteractiveMessage message={message} />
  }

  if (message.message_type?.toLowerCase() === 'template') {
    let templateMediaUrl = (message as any).mediaUrl || (message as any).media_url
    
    if (!templateMediaUrl && message.content_text) {
      try {
        const contentData = JSON.parse(message.content_text)
        if (contentData.headerMediaUrl) {
          templateMediaUrl = contentData.headerMediaUrl
        }
      } catch (e) {
      }
    }
    
    try {
      const contentText = message.content_text || '{}'
      const templateData = JSON.parse(contentText)
      const templateName = templateData?.templateName || templateData?.template_name ||
        templateData?.template?.name || templateData?.name

      if (templateName && typeof templateName === 'string') {
        const finalHeaderMediaUrl = templateMediaUrl || templateData.headerMediaUrl || undefined
        
        let variables = templateData.variables
        
        if (!variables && templateData.parameters && Array.isArray(templateData.parameters)) {
          variables = {}
          templateData.parameters.forEach((param: any, index: number) => {
            const value = typeof param === 'string' ? param : (param?.text || param?.value || String(param))
            variables[String(index + 1)] = value
          })
        }
        
        if ((!variables || Object.keys(variables).length === 0) && templateData.namedParameters && typeof templateData.namedParameters === 'object') {
          variables = templateData.namedParameters
        }

        const sentComponents = templateData.components || templateData.template?.components
        if ((!variables || Object.keys(variables).length === 0) && Array.isArray(sentComponents)) {
          const bodyComponent = sentComponents.find((c: any) =>
            (c.type === 'body' || c.type === 'BODY') && c.parameters && Array.isArray(c.parameters)
          )
          
          if (bodyComponent && bodyComponent.parameters) {
            const hasNamedParams = bodyComponent.parameters.some((p: any) => p.parameter_name)
            
            if (hasNamedParams) {
              variables = {}
              bodyComponent.parameters.forEach((param: any) => {
                if (param.parameter_name) {
                  const value = param.text || param.value || String(param)
                  variables[param.parameter_name] = value
                }
              })
            } else {
              variables = {}
              bodyComponent.parameters.forEach((param: any, index: number) => {
                const value = param.text || param.value || String(param)
                variables[String(index + 1)] = value
              })
            }
          }
          if ((!variables || Object.keys(variables).length === 0)) {
            const codeButton = sentComponents.find((c: { type?: string; sub_type?: string; parameters?: Array<{ text?: string }> }) =>
              c?.type?.toLowerCase() === 'button' &&
              c?.sub_type?.toLowerCase() === 'copy_code' &&
              Array.isArray(c.parameters) && c.parameters.length > 0
            )
            const code = codeButton?.parameters?.[0]?.text
            if (code) variables = { '1': code }
          }
        }
        
        const applyVariables = (text: string, vars: Record<string, any>): string => {
          let result = text
          const keys = Object.keys(vars)
          const isPositional = keys.every(k => /^\d+$/.test(k))
          if (isPositional) {
            keys.sort((a, b) => parseInt(a) - parseInt(b)).forEach((k, idx) => {
              result = result.replace(new RegExp(`\\{\\{${idx + 1}\\}\\}`, 'g'), String(vars[k]))
            })
          } else {
            Object.entries(vars).forEach(([k, v]) => {
              result = result.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), String(v))
            })
          }
          return result
        }

        const snapshot = templateData.snapshot
        if (snapshot && snapshot.body) {
          const bodyText = variables && Object.keys(variables).length > 0
            ? applyVariables(snapshot.body, variables)
            : snapshot.body
          const headerText = snapshot.header
            ? (variables && Object.keys(variables).length > 0 ? applyVariables(snapshot.header, variables) : snapshot.header)
            : null
          const buttonsComp = snapshot.buttons?.buttons || snapshot.buttons?.items || null

          return (
            <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden max-w-xs">
              {headerText && (
                <div className="px-3 pt-3 pb-1 text-sm font-semibold text-gray-900">
                  {headerText}
                </div>
              )}
              {finalHeaderMediaUrl && (
                <div className="relative bg-gray-50">
                  <img
                    src={finalHeaderMediaUrl}
                    alt="Header"
                    loading="lazy"
                    decoding="async"
                    className="w-full max-h-40 object-cover"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                  />
                </div>
              )}
              <div className="px-3 py-2 text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                {bodyText}
              </div>
              {snapshot.footer && (
                <div className="px-3 pb-2 text-xs text-gray-500">
                  {snapshot.footer}
                </div>
              )}
              {buttonsComp && buttonsComp.length > 0 && (
                <div className="border-t border-gray-200 divide-y divide-gray-100">
                  {buttonsComp.map((btn: any, idx: number) => (
                    <div key={idx} className="px-3 py-2 text-center text-sm font-medium text-blue-600">
                      {btn.text || btn.title || btn.reply?.title || ''}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        }

        return (
          <TemplateMessagePreview
            templateName={templateName}
            language={typeof templateData.language === 'string' ? templateData.language : templateData.language?.code || templateData.template?.language?.code}
            variables={variables}
            businessAccountId={businessAccountId}
            headerMediaUrl={finalHeaderMediaUrl}
            carouselMediaUrls={templateData.carouselMediaUrls && typeof templateData.carouselMediaUrls === 'object' ? templateData.carouselMediaUrls : undefined}
          />
        )
      }
      return <span className="text-sm text-gray-600">Template message</span>
    } catch (e) {
      const contentText = message.content_text || ''
      if (contentText.includes('@template') || contentText.toLowerCase().includes('template')) {
        return (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>📋</span>
            <span>Template Message</span>
          </div>
        )
      }
      return (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span>📋</span>
          <span>Template: {contentText || 'Template message'}</span>
        </div>
      )
    }
  }

  if (message.message_type?.toLowerCase() === 'location') {
    return <LocationMessage message={message} />
  }

  if (message.message_type?.toLowerCase() === 'story_mention') {
    const mediaUrl = (message as any).mediaUrl || (message as any).media_url
    return (
      <div className="w-[280px] overflow-hidden rounded-2xl">
        <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400">
          <InstagramIcon className="h-4 w-4 text-white" />
          <span className="text-xs font-semibold text-white">Mentioned you in their story</span>
        </div>
        {mediaUrl ? (
          <div className="relative bg-black">
            <img
              src={mediaUrl}
              alt="Story mention"
              loading="lazy"
              decoding="async"
              className="w-full h-[200px] object-cover cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => window.open(mediaUrl, '_blank')}
              onError={(e) => {
                const target = e.target as HTMLImageElement
                target.style.display = 'none'
                const parent = target.parentElement
                if (parent && !parent.querySelector('.error-fallback')) {
                  const fallback = document.createElement('div')
                  fallback.className = 'error-fallback flex flex-col items-center justify-center h-[120px] bg-gradient-to-br from-purple-50 to-pink-50 text-gray-400'
                  fallback.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect width="20" height="20" x="2" y="2" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" x2="17.51" y1="6.5" y2="6.5"/></svg><span class="text-xs mt-1">Story expired</span>'
                  parent.appendChild(fallback)
                }
              }}
            />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-[120px] bg-gradient-to-br from-purple-50 to-pink-50 text-gray-400">
            <InstagramIcon className="h-8 w-8" />
            <span className="text-xs mt-1">Story media unavailable</span>
          </div>
        )}
        {message.content_text && message.content_text.trim() !== '' && (
          <div className="px-3 py-2 bg-white text-sm text-gray-800">
            {parseWhatsAppFormatting(message.content_text)}
          </div>
        )}
      </div>
    )
  }

  if (message.message_type?.toLowerCase() === 'story_reply') {
    const mediaUrl = (message as any).mediaUrl || (message as any).media_url
    const replyText = message.content_text || ''
    return (
      <div className="w-[280px] overflow-hidden rounded-2xl">
        <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400">
          <MessageCircleReply className="h-4 w-4 text-white" />
          <span className="text-xs font-semibold text-white">Replied to your story</span>
        </div>
        {mediaUrl && (
          <div className="relative bg-black">
            <img
              src={mediaUrl}
              alt="Story"
              loading="lazy"
              decoding="async"
              className="w-full h-[140px] object-cover opacity-80"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
            />
          </div>
        )}
        {replyText && replyText.trim() !== '' ? (
          <div className="px-3 py-2.5 bg-white">
            <p className="text-sm text-gray-900 whitespace-pre-wrap leading-relaxed">
              {parseWhatsAppFormatting(replyText)}
            </p>
          </div>
        ) : (
          <div className="px-3 py-2.5 bg-white">
            <p className="text-sm text-gray-400 italic">Reacted to your story</p>
          </div>
        )}
      </div>
    )
  }

  if (['contacts', 'system'].includes(message.message_type?.toLowerCase() || '')) {
    return (
      <div className="text-sm whitespace-pre-wrap leading-relaxed text-gray-900">
        {parseWhatsAppFormatting(message.content_text || '')}
      </div>
    )
  }

  const unsupportedInfo = getUnsupportedMessageInfo(message)
  if (unsupportedInfo) {
    return (
      <div className="flex items-start gap-2 max-w-[300px]" data-testid="inbox-unsupported-message">
        <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0 text-amber-500" aria-hidden />
        <div className="min-w-0 space-y-1">
          <p className="text-sm font-medium text-gray-900">{unsupportedInfo.title}</p>
          <p className="text-xs leading-relaxed text-gray-600">{unsupportedInfo.body}</p>
        </div>
      </div>
    )
  }

  const mediaType = message.media_type || message.message_type
  const mediaUrl = (message as any).mediaUrl || (message as any).media_url

  if (!mediaUrl && message.message_type?.toLowerCase() !== 'text') {
    return (
      <div className="flex items-center gap-2">
        <Paperclip className="h-4 w-4" />
        <span className="text-sm">
          {mediaType || 'Attachment'}
        </span>
      </div>
    )
  }

  const finalMediaType = mediaType || (mediaUrl ? message.message_type : null)

  if (mediaUrl && !finalMediaType) {
    return (
      <div className="space-y-2 p-2">
        <div className="flex items-center gap-2 p-3 bg-white rounded-lg shadow-sm border border-gray-200">
          {getMediaIcon('document')}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">
              {(message as any).mediaFilename || 'Media File'}
            </p>
            <p className="text-xs text-muted-foreground">MEDIA</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.open(mediaUrl, '_blank')}
          >
            <Download className="h-4 w-4" />
          </Button>
        </div>
        {message.content_text && (
          <p className="text-sm whitespace-pre-wrap px-2 text-gray-900">
            {parseWhatsAppFormatting(message.content_text)}
          </p>
        )}
      </div>
    )
  }

  switch (finalMediaType?.toLowerCase()) {
    case 'image':
      return (
        <div className="space-y-2 p-2">
          <ImagePreview mediaUrl={mediaUrl} alt="Image" message={message} />
          {message.content_text && (
            <p className="text-sm whitespace-pre-wrap px-2 text-gray-900">
              {parseWhatsAppFormatting(message.content_text)}
            </p>
          )}
        </div>
      )
    case 'video':
      return (
        <div className="space-y-2 p-2">
          <div className="relative bg-white rounded-lg overflow-hidden shadow-sm border border-gray-200">
            <video
              src={mediaUrl}
              controls
              className="max-w-xs max-h-48 w-full h-auto"
              preload="metadata"
            >
              Your browser does not support the video tag.
            </video>
          </div>
          {message.content_text && (
            <p className="text-sm whitespace-pre-wrap px-2 text-gray-900">
              {parseWhatsAppFormatting(message.content_text)}
            </p>
          )}
        </div>
      )
    case 'audio':
      return (
        <div className="space-y-2 p-4 min-w-[250px]">
          <audio
            src={mediaUrl}
            controls
            className="w-full"
            preload="metadata"
            style={{ minHeight: '48px', display: 'block', width: '100%' }}
            onError={(e) => {
            }}
          >
            Your browser does not support the audio tag.
          </audio>
          {message.content_text && (
            <p className="text-sm whitespace-pre-wrap px-2 pt-2 text-gray-900">
              {parseWhatsAppFormatting(message.content_text)}
            </p>
          )}
        </div>
      )
    case 'sticker':
      return (
        <div className="space-y-2 p-2">
          <ImagePreview mediaUrl={mediaUrl} alt="Sticker" message={message} maxHeightClass="max-h-64" />
          {message.content_text && (
            <p className="text-sm whitespace-pre-wrap px-2 text-gray-900">
              {parseWhatsAppFormatting(message.content_text)}
            </p>
          )}
        </div>
      )
    case 'document':
    default:
      return (
        <DocumentMessage message={message} mediaUrl={mediaUrl} mediaType={mediaType} />
      )
  }
}
